import {
  addDoc,
  collection,
  doc,
  getDoc,
  increment,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../firebaseConfig";

const SESSION_STORAGE_KEY = "nn_analytics_session_id";

function generateSessionId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `session_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function getAnalyticsSessionId() {
  if (typeof window === "undefined") {
    return generateSessionId();
  }

  const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (existing) return existing;

  const created = generateSessionId();
  window.sessionStorage.setItem(SESSION_STORAGE_KEY, created);
  return created;
}

function getCurrentPath() {
  if (typeof window === "undefined") return null;
  return window.location?.pathname || null;
}

function getUserAgent() {
  if (typeof navigator === "undefined") return null;
  return navigator.userAgent || null;
}

function getVisitSource() {
  if (typeof document === "undefined") return "direct";

  const referrer = document.referrer;
  if (!referrer) return "direct";

  try {
    const refUrl = new URL(referrer);
    const currentOrigin = typeof window !== "undefined" ? window.location.origin : "";
    return refUrl.origin === currentOrigin ? "internal" : "referral";
  } catch (error) {
    return "referral";
  }
}

async function touchAnalyticsResumen(empresaId, updates) {
  const resumenRef = doc(db, "empresas", empresaId, "analytics", "resumen");

  await setDoc(
    resumenRef,
    {
      visitasTotales: 0,
      clicksWhatsappTotales: 0,
      ultimoAcceso: null,
      ultimaInteraccionWhatsapp: null,
      updatedAt: serverTimestamp(),
      ...updates,
    },
    { merge: true }
  );
}

export async function ensureAnalyticsResumen(empresaId) {
  if (!empresaId) return;

  const resumenRef = doc(db, "empresas", empresaId, "analytics", "resumen");
  const snapshot = await getDoc(resumenRef);

  if (!snapshot.exists()) {
    await setDoc(resumenRef, {
      visitasTotales: 0,
      clicksWhatsappTotales: 0,
      ultimoAcceso: null,
      ultimaInteraccionWhatsapp: null,
      updatedAt: serverTimestamp(),
    });
  }
}

export async function trackVisit({ empresaId, empresaSlug }) {
  if (!empresaId) return;

  const sessionId = getAnalyticsSessionId();
  const path = getCurrentPath();

  await addDoc(collection(db, "empresas", empresaId, "visitas"), {
    createdAt: serverTimestamp(),
    empresaSlug: empresaSlug || null,
    path,
    source: getVisitSource(),
    sessionId,
    userAgent: getUserAgent(),
    country: null,
    region: null,
    city: null,
    ipHash: null,
  });

  await touchAnalyticsResumen(empresaId, {
    visitasTotales: increment(1),
    ultimoAcceso: serverTimestamp(),
  });
}

export async function trackWhatsappClick({ empresaId, empresaSlug }) {
  if (!empresaId) return;

  const sessionId = getAnalyticsSessionId();

  await addDoc(collection(db, "empresas", empresaId, "whatsappClicks"), {
    createdAt: serverTimestamp(),
    empresaSlug: empresaSlug || null,
    path: getCurrentPath(),
    sessionId,
    country: null,
    region: null,
    city: null,
    ipHash: null,
  });

  await touchAnalyticsResumen(empresaId, {
    clicksWhatsappTotales: increment(1),
    ultimaInteraccionWhatsapp: serverTimestamp(),
  });
}

export async function trackCheckoutClick({
  empresaId,
  empresaSlug,
  total,
  items,
  customerName,
  deliveryMethod,
  address,
  neighborhood,
  reference,
  paymentMethod,
}) {
  if (!empresaId) return;

  await addDoc(collection(db, "empresas", empresaId, "pedidosFinalizados"), {
    createdAt: serverTimestamp(),
    empresaSlug: empresaSlug || null,
    path: getCurrentPath(),
    sessionId: getAnalyticsSessionId(),
    customerName: customerName || null,
    deliveryMethod: deliveryMethod || null,
    address: address || null,
    neighborhood: neighborhood || null,
    reference: reference || null,
    paymentMethod: paymentMethod || null,
    total: Number(total || 0),
    items: Array.isArray(items)
      ? items.map((item) => ({
          id: item.id || null,
          nombre: item.nombre || "",
          cantidad: Number(item.cantidad || 0),
          precio: Number(item.precio || 0),
          talle: item.talle || null,
        }))
      : [],
  });
}
